import React, { useEffect, useRef, useState } from "react";
import { Phone } from "./Hero";
import { art } from "./config";

// One conversation, recorded as alternating turns. `secs` is each clip's
// length, used for the overall progress bar.
const TRACKS = [
  { who: "leo", src: "/audio/dialogue-1.mp3", secs: 10.92 },
  { who: "kid", src: "/audio/child-1.m4a", secs: 4.01 },
  { who: "leo", src: "/audio/dialogue-2.mp3", secs: 8.5 },
  { who: "kid", src: "/audio/child-2.m4a", secs: 4.05 },
];

const NUM_BARS = 28;
const GAP_MS = 450;
const TOTAL_SECS = TRACKS.reduce((t, c) => t + c.secs, 0);
const STARTS = TRACKS.map((_, n) => TRACKS.slice(0, n).reduce((t, c) => t + c.secs, 0));

const clock = (secs) => {
  const s = Math.max(0, Math.round(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export default function Listen() {
  // track: -1 not started, 0..n-1 that track, n finished
  const [track, setTrack] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0); // seconds into the whole conversation

  const audioRef = useRef(null);
  const trackRef = useRef(-1);
  const gapRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(0);
  const barsRef = useRef(null);
  const screenRef = useRef(null);

  // Wire the <audio> element into an analyser so the bars and Leo's glow
  // follow the real voice. Must happen after a user gesture.
  const ensureAnalyser = () => {
    if (ctxRef.current) {
      if (ctxRef.current.state === "suspended") ctxRef.current.resume();
      return;
    }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    try {
      const ctx = new AC();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.72;
      const source = ctx.createMediaElementSource(audioRef.current);
      source.connect(analyser);
      analyser.connect(ctx.destination);
      ctxRef.current = ctx;
      analyserRef.current = analyser;
    } catch (e) {
      // No analyser: audio still plays, the bars fall back to CSS motion.
    }
  };

  const draw = () => {
    const analyser = analyserRef.current;
    const bars = barsRef.current?.children;
    const a = audioRef.current;
    const n = trackRef.current;
    if (a && n >= 0 && n < TRACKS.length) {
      const t = Math.round((STARTS[n] + a.currentTime) * 10) / 10;
      setElapsed((old) => (old === t ? old : t));
    }
    if (analyser && bars) {
      const data = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(data);
      const usable = Math.floor(data.length * 0.65); // where speech lives
      let total = 0;
      for (let i = 0; i < NUM_BARS; i++) {
        // mirror around the centre so it reads like a voice, not an EQ
        const k = Math.abs(i - (NUM_BARS - 1) / 2) / (NUM_BARS / 2);
        const bin = Math.floor(k * usable);
        const v = data[bin] / 255;
        total += v;
        bars[i].style.transform = `scaleY(${Math.max(0.08, v * (1.15 - k * 0.5))})`;
      }
      const amp = Math.min(1, (total / NUM_BARS) * 2.2);
      screenRef.current?.style.setProperty("--amp", amp.toFixed(3));
    }
    rafRef.current = requestAnimationFrame(draw);
  };

  const stopDrawing = () => {
    cancelAnimationFrame(rafRef.current);
    const bars = barsRef.current?.children;
    if (bars) Array.from(bars).forEach((b) => (b.style.transform = ""));
    screenRef.current?.style.setProperty("--amp", "0");
  };

  const playAt = (n) => {
    clearTimeout(gapRef.current);
    trackRef.current = n;
    setTrack(n);
    if (n >= TRACKS.length) {
      setElapsed(TOTAL_SECS);
      setPlaying(false);
      stopDrawing();
      return;
    }
    const a = audioRef.current;
    a.src = process.env.PUBLIC_URL + TRACKS[n].src;
    ensureAnalyser();
    setPlaying(true);
    a.play()
      .then(() => {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(draw);
      })
      .catch(() => setPlaying(false));
  };

  const toggle = () => {
    const a = audioRef.current;
    if (playing) {
      a.pause();
      clearTimeout(gapRef.current);
      setPlaying(false);
      stopDrawing();
      return;
    }
    window.posthog?.capture?.("listen_demo_play");
    const n = trackRef.current;
    if (n < 0 || n >= TRACKS.length) {
      setElapsed(0);
      playAt(0);
    } else if (a.currentTime > 0 && !a.ended) {
      ensureAnalyser();
      setPlaying(true);
      a.play()
        .then(() => {
          rafRef.current = requestAnimationFrame(draw);
        })
        .catch(() => setPlaying(false));
    } else {
      playAt(n + 1);
    }
  };

  useEffect(() => {
    const a = audioRef.current;
    const onEnded = () => {
      stopDrawing();
      gapRef.current = setTimeout(() => playAt(trackRef.current + 1), GAP_MS);
    };
    a.addEventListener("ended", onEnded);
    // The hero "Hear us talk" button scrolls here and asks us to start.
    const onListen = () => {
      if (trackRef.current < 0 || trackRef.current >= TRACKS.length) {
        setElapsed(0);
        playAt(0);
      }
    };
    window.addEventListener("leo:listen", onListen);
    return () => {
      a.removeEventListener("ended", onEnded);
      window.removeEventListener("leo:listen", onListen);
      clearTimeout(gapRef.current);
      cancelAnimationFrame(rafRef.current);
      a.pause();
      ctxRef.current?.close?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finished = track >= TRACKS.length;
  const speaker = track >= 0 && !finished ? TRACKS[track].who : null;
  const status = playing
    ? speaker === "kid"
      ? "Leo is listening…"
      : "Leo is talking"
    : finished
    ? "Play it again?"
    : track >= 0
    ? "Paused"
    : "Tap play to listen";

  return (
    <section id="listen" className="listen" data-testid="listen-section">
      <div className="listen-card">
        <div className="wrap listen-grid">
          <div>
            <span className="eyebrow rv">Listen in</span>
            <h2 className="h2 rv" style={{ marginTop: 18, "--d": "80ms" }}>
              No worksheets.
              <br />
              Just <span className="said">two mates</span> working it out.
            </h2>
            <p className="lede rv" style={{ marginTop: 22, "--d": "160ms" }}>
              Your child talks to me out loud, like they would to a friend. I think out loud
              too — and when I get stuck, <strong>they're the one who knows the answer.</strong>{" "}
              Press play and hear how it goes.
            </p>
            <p className="listen-quote rv" style={{ "--d": "240ms" }}>
              Psychologists call it the protégé effect: children try harder, and understand
              more, when they're teaching someone else.
            </p>
          </div>

          <div className="lstage rv" style={{ "--d": "120ms" }}>
            <Phone className="lphone">
              <div
                className={`lscreen ${playing ? "is-live" : ""} ${speaker === "kid" ? "is-kid" : ""}`}
                ref={screenRef}
              >
                <div className="lscreen-top">
                  <b>Leo</b>
                  <span className={`chat-live ${playing ? "is-on" : ""}`}>{status}</span>
                </div>

                <div className="lleo">
                  <span className="lglow" aria-hidden />
                  <span className="lring" aria-hidden />
                  <span className="lring" aria-hidden />
                  <img className="lleo-talk" src={art("leo-standing-600.png")} alt="Leo" />
                  <img className="lleo-listen" src={art("leo-thinking-600.png")} alt="" />
                </div>

                <div className="lwave" ref={barsRef} aria-hidden>
                  {Array.from({ length: NUM_BARS }, (_, i) => (
                    <i key={i} />
                  ))}
                </div>

                <button
                  type="button"
                  className="play lplay"
                  onClick={toggle}
                  aria-label={playing ? "Pause conversation" : "Play conversation"}
                  data-testid="listen-play"
                >
                  {playing ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <rect x="6" y="5" width="4" height="14" rx="1.2" />
                      <rect x="14" y="5" width="4" height="14" rx="1.2" />
                    </svg>
                  ) : (
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M8 5.5v13l10.5-6.5z" />
                    </svg>
                  )}
                </button>
              </div>
            </Phone>

            <div className="lprogress" aria-hidden>
              <span className="lprogress-bar">
                <i style={{ transform: `scaleX(${elapsed / TOTAL_SECS})` }} />
              </span>
              <span className="lprogress-time">
                {clock(elapsed)} / {clock(TOTAL_SECS)}
              </span>
            </div>
            <audio ref={audioRef} preload="none" />
          </div>
        </div>
      </div>
    </section>
  );
}
